import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Lock, Heart, MessageCircle, Compass, Eye, Quote, Check } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { submitLead } from "@/lib/api";

const HERO_CLARITY_ART = "https://customer-assets.emergentagent.com/job_dc79c178-2c6a-4f41-afbc-479359c635b4/artifacts/4fqkkgoz_marrsing%20Clarity%20and%20Couples%20with%20Logo.png";
const CLARITY_GROUP = "https://customer-assets.emergentagent.com/job_reveal-ready/artifacts/ke60ffc1_ChatGPT%20Image%20Apr%2027%2C%202026%20at%2007_22_43%20PM.png";
const LOGO_PILLARS = "https://customer-assets.emergentagent.com/job_reveal-ready/artifacts/jx4z6a8m_ChatGPT%20Image%20Apr%2029%2C%202026%20at%2002_42_41%20PM.png";

export default function Landing() {
  return (
    <div style={{ overflowX: "hidden", background: "transparent" }}>
      <Nav variant="light" />
      <Hero />
      <TrustBar />
      <Problem />
      <How />
      <Pillars />
      <Intelligence />
      <Pricing />
      <Testimonials />
      <Waitlist />
      <FinalCTA />
      <Footer />
    </div>
  );
}

/* ────────────────────────────── HERO ────────────────────────────── */
function Hero() {
  return (
    <section
      data-testid="hero-section"
      style={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        padding: "128px 32px 80px",
        overflow: "hidden",
        background: `
          radial-gradient(circle at 82% 12%, rgba(111,199,242,0.32), transparent 38%),
          radial-gradient(circle at 8% 88%, rgba(127,205,187,0.22), transparent 36%),
          linear-gradient(155deg, #FAF8F3 0%, #EEF8FC 50%, #F8FCFF 100%)
        `,
      }}
      className="mc-grain"
    >
      <div style={{
        position: "absolute",
        bottom: -150,
        right: -100,
        width: 720,
        height: 720,
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(111,199,242,0.25) 0%, transparent 70%)",
        pointerEvents: "none",
      }} className="mc-pulse" />

      <div style={{
        position: "absolute", left: 0, right: 0, bottom: 0,
        height: 1, background: "linear-gradient(90deg, transparent, var(--mc-blue), transparent)",
      }} />

      <div style={{
        position: "relative", zIndex: 2,
        maxWidth: 1240, margin: "0 auto", width: "100%",
        display: "grid",
        gridTemplateColumns: "minmax(0,1.15fr) minmax(0,1fr)",
        gap: 64,
        alignItems: "center",
      }} className="mc-hero-grid">

        <div>
          <div className="mc-fade-up-1" style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
            <span style={{ width: 44, height: 1, background: "var(--mc-blue)" }} />
            <span className="mc-eyebrow">World's first trademark relationship clarity system</span>
          </div>

          <h1 className="mc-h1 mc-fade-up-2" style={{ fontSize: "clamp(46px, 6.4vw, 78px)", marginBottom: 24 }}>
            It's not love<br />
            that's missing.<br />
            It's <em>clarity.</em>
          </h1>

          <div className="mc-fade-up-3" style={{ maxWidth: 560, marginBottom: 44 }}>
            <p style={{ fontSize: 18.5, lineHeight: 1.6, color: "var(--mc-text)", fontWeight: 600, marginBottom: 16 }}>
              What if you could see your relationship with greater clarity&mdash;before misunderstandings create distance?
            </p>
            <p style={{ fontSize: 16.5, lineHeight: 1.75, color: "var(--mc-text-2)" }}>
              Every relationship is built on thousands of conversations, assumptions, and expectations&mdash;many of them never spoken or expressed aloud. MarrsingClause® helps you discover the alignment gaps, communication patterns, and unspoken expectations shaping your relationship today, so you can have the conversations that deepen connection, and create a more intentional future together.
            </p>
          </div>

          <div className="mc-fade-up-4" style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 440 }}>
            <Link to="/quiz" className="mc-btn-primary" data-testid="hero-primary-cta">
              Discover Your Relationship Clarity
              <ArrowRight size={16} />
            </Link>
            <Link to="/quiz" className="mc-btn-secondary" data-testid="hero-secondary-cta">
              Take the Free Clarity Quiz
            </Link>
          </div>

          <p className="mc-fade-up-5 font-mono" style={{
            marginTop: 24, fontSize: 11, letterSpacing: "0.08em",
            color: "var(--mc-text-3)", textTransform: "uppercase",
          }}>
            Private &amp; confidential · responses locked until reveal
          </p>
        </div>

        <div className="mc-fade-up-4" style={{ position: "relative" }}>
          {/* Clarity artwork — sits behind & upper-right of the Alignment Intelligence card */}
          <img
            src={HERO_CLARITY_ART}
            alt="Marrsing Clarity — Positive Relationship Transformation"
            data-testid="hero-clarity-art"
            className="mc-hero-clarity"
            style={{
              position: "absolute",
              top: -72, right: -64,
              width: 440, maxWidth: "94%",
              borderRadius: 28,
              opacity: 0.88,
              zIndex: 1,
              pointerEvents: "none",
              boxShadow: "0 26px 64px rgba(31,117,168,0.18)",
            }}
          />
          {/* Mock card — Alignment intelligence preview */}
          <div style={{
            background: "rgba(255,255,255,0.7)",
            border: "1px solid var(--mc-border-blue)",
            borderRadius: 28,
            padding: "36px 32px",
            boxShadow: "0 28px 60px rgba(31,117,168,0.18)",
            backdropFilter: "blur(8px)",
            position: "relative",
            zIndex: 2,
          }}>
            <div className="font-mono" style={{
              fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
              color: "var(--mc-blue-deep)", marginBottom: 22, fontWeight: 600,
            }}>
              Marrsing Alignment Intelligence™
            </div>
            {[
              { n: 1, label: "Reflect", body: "Each partner answers guided questions in complete honesty and privacy." },
              { n: 2, label: "Reveal",  body: "Responses stay locked until both partners are ready for the shared reveal." },
              { n: 3, label: "Align",   body: "Receive your Alignment Intelligence™ and begin the conversations that matter." },
            ].map((r, idx) => (
              <div key={r.n} style={{
                display: "flex", alignItems: "flex-start", gap: 16,
                padding: "18px 0",
                borderBottom: idx < 2 ? "1px solid rgba(47,158,216,0.16)" : "none",
              }}>
                <div style={{
                  width: 30, height: 30, borderRadius: "50%",
                  border: "1px solid var(--mc-blue)",
                  background: "var(--mc-blue-pale)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                  fontSize: 12, color: "var(--mc-blue-deep)", fontWeight: 600,
                  fontFamily: "var(--font-mono)",
                }}>{r.n}</div>
                <div>
                  <strong style={{ display: "block", fontSize: 15, color: "var(--mc-text)", marginBottom: 4 }}>{r.label}</strong>
                  <p style={{ fontSize: 13, color: "var(--mc-text-3)", lineHeight: 1.6, margin: 0 }}>{r.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 920px) {
          .mc-hero-grid { grid-template-columns: 1fr !important; gap: 56px !important; }
        }
        @media (max-width: 768px) {
          .mc-hero-clarity {
            position: relative !important;
            top: 0 !important; right: 0 !important;
            width: 100% !important; max-width: 100% !important;
            margin-bottom: 18px !important;
            opacity: 0.95 !important;
          }
        }
      `}</style>
    </section>
  );
}

/* ────────────────────────────── TRUST BAR ────────────────────────────── */
function TrustBar() {
  const items = [
    "World's first trademark relationship system",
    "Created by bestselling author Angella Watkis Francis",
    "3 minutes · completely free",
    "Personalized results across 3 pillars",
  ];
  return (
    <div data-testid="trust-bar" style={{
      background: "white",
      borderTop: "0.5px solid rgba(30,27,22,0.06)",
      borderBottom: "0.5px solid rgba(30,27,22,0.06)",
      padding: "22px 24px",
    }}>
      <div style={{
        maxWidth: 1100, margin: "0 auto",
        display: "flex", flexWrap: "wrap",
        alignItems: "center", justifyContent: "center", gap: 36,
      }}>
        {items.map((t) => (
          <div key={t} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: "var(--mc-text-3)" }} className="font-mono">
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--mc-teal)" }} />
            {t}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────────── PROBLEM ────────────────────────────── */
function Problem() {
  return (
    <section data-testid="problem-section" style={{ padding: "104px 32px", background: "transparent" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 72, alignItems: "center" }} className="mc-two-col">
          <div>
            <Eyebrow text="The Reality" />
            <h2 className="mc-h2" style={{ fontSize: "clamp(34px, 4.2vw, 52px)", marginTop: 14, marginBottom: 24 }}>
              Most relationships don't fail<br />from lack of <em>love.</em>
            </h2>
            <div style={{ fontSize: 17, lineHeight: 1.8, color: "var(--mc-text-2)" }}>
              <p>They struggle because of misaligned expectations, unspoken assumptions, and conversations that never happen — until the pain becomes unavoidable.</p>
              <p style={{ marginTop: 18 }}>Couples spend years building lives together without ever mapping the territory of each other's deepest expectations about finances, family, communication, conflict, and future vision.</p>
              <p style={{ marginTop: 18 }}>MarrsingClause® was built to change that. Not with therapy. Not with guesswork. With structured, intelligent clarity — before you need it most.</p>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }} className="mc-problem-cards">
            {[
              { word: "Clarity",       body: "Reveal expectations before they become emotional pressure points." },
              { word: "Communication", body: "Identify patterns that need attention before conflict escalates." },
              { word: "Alignment",     body: "Understand where your values, timing, and future vision connect." },
              { word: "Intention",     body: "Move from assumption to guided, honest conversation." },
            ].map((s) => (
              <div key={s.word} data-testid={`stat-${s.word.toLowerCase()}`} style={{
                background: "linear-gradient(145deg, var(--mc-blue-pale) 0%, white 100%)",
                padding: "30px 26px",
                border: "1px solid var(--mc-border-blue)",
                borderRadius: 22,
              }}>
                <div className="font-serif" style={{
                  fontSize: 30, fontWeight: 500, color: "var(--mc-blue-deep)", lineHeight: 1.05,
                }}>{s.word}</div>
                <p style={{ fontSize: 14, color: "var(--mc-text-3)", lineHeight: 1.55, marginTop: 10 }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 920px) {
          .mc-two-col { grid-template-columns: 1fr !important; gap: 48px !important; }
        }
        @media (max-width: 640px) {
          .mc-problem-cards { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

/* ────────────────────────────── HOW IT WORKS ────────────────────────────── */
function How() {
  const steps = [
    { n: "01", t: "Reflect", body: "Each partner enters their private reflection space and answers guided questions with complete honesty. There is no judgment, no observation — just you and your own truth." },
    { n: "02", t: "Reveal",  body: "Your responses are securely locked until both partners are ready. The anticipation is part of the design — it creates the space for a real, intentional conversation." },
    { n: "03", t: "Align",   body: "Receive your personalized Marrsing Alignment Intelligence™. See where you align, where you diverge, and receive guided prompts for the conversations that follow." },
  ];
  return (
    <section id="how" data-testid="how-section" style={{
      padding: "104px 32px",
      background: "linear-gradient(180deg, transparent 0%, rgba(238,248,252,0.55) 100%)",
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Eyebrow text="The Experience" />
        <h2 className="mc-h2" style={{ fontSize: "clamp(34px, 4.2vw, 52px)", marginTop: 14, marginBottom: 56, maxWidth: 760 }}>
          A structured path to <em>real clarity.</em>
        </h2>

        <div style={{
          display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20,
        }} className="mc-three-col">
          {steps.map((s) => (
            <div key={s.n} data-testid={`step-${s.n}`} style={{
              position: "relative",
              background: "white",
              padding: "44px 34px 38px",
              borderRadius: 26,
              border: "1px solid var(--mc-border-blue)",
              boxShadow: "0 14px 32px rgba(31,117,168,0.07)",
              transition: "transform 0.25s, box-shadow 0.25s",
            }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-6px)"; e.currentTarget.style.boxShadow = "0 24px 50px rgba(31,117,168,0.14)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 14px 32px rgba(31,117,168,0.07)"; }}
            >
              <div style={{
                position: "absolute", top: 0, left: 24, right: 24, height: 3,
                background: "linear-gradient(90deg, var(--mc-blue-light), var(--mc-teal))",
                borderRadius: 999,
              }} />
              <div className="font-serif" style={{
                fontSize: 64, fontWeight: 300,
                color: "rgba(111,199,242,0.42)", lineHeight: 1, marginBottom: 14,
              }}>{s.n}</div>
              <h3 className="font-serif" style={{
                fontSize: 30, fontWeight: 500, color: "var(--mc-blue-deep)", marginBottom: 14,
              }}>{s.t}</h3>
              <p style={{ fontSize: 15, color: "var(--mc-text-3)", lineHeight: 1.7 }}>{s.body}</p>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 920px) {
          .mc-three-col { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

/* ────────────────────────────── PILLARS ────────────────────────────── */
function Pillars() {
  const pillars = [
    {
      icon: <Heart size={20} />,
      accent: "var(--mc-blue)",
      bg: "rgba(111,199,242,0.14)",
      name: "Clarity",
      body: "Before there is \"us,\" there is one mind. Clarity is the practice of knowing that mind honestly — your values, your needs, your non-negotiables.",
      q: "Do you know what you actually want from this relationship — not what you think you should want?",
    },
    {
      icon: <MessageCircle size={20} />,
      accent: "var(--mc-teal)",
      bg: "rgba(127,205,187,0.18)",
      name: "Communication",
      body: "The pause before we respond. Communication is the bridge between what you know about yourself and what your partner can understand and act on.",
      q: "Are you having the conversations that matter — or the ones that feel safe?",
    },
    {
      icon: <Compass size={20} />,
      accent: "var(--mc-gold)",
      bg: "rgba(215,181,109,0.18)",
      name: "Alignment",
      body: "Alignment is not compatibility. It asks: are you moving in the same direction, with the same intention, in a way that honours both of you?",
      q: "Are you and your partner building toward the same future — or assuming you are?",
    },
  ];

  return (
    <section id="pillars" data-testid="pillars-section" style={{
      padding: "104px 32px",
      background: `
        radial-gradient(circle at 80% 10%, rgba(111,199,242,0.18), transparent 38%),
        linear-gradient(180deg, #EEF8FC 0%, #FAF8F3 100%)
      `,
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Eyebrow text="The MarrsingClause® Framework" />
        <h2 className="mc-h2" style={{ fontSize: "clamp(34px, 4.2vw, 52px)", marginTop: 14, marginBottom: 14, maxWidth: 780 }}>
          Three pillars.<br />One <em>living</em> system.
        </h2>
        <p style={{ fontSize: 16, color: "var(--mc-text-2)", maxWidth: 580, lineHeight: 1.75, marginBottom: 48 }}>
          The Clarity Quiz measures where you stand on each pillar — and gives you a personalized guide to what needs your attention most.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }} className="mc-three-col">
          {pillars.map((p) => (
            <div key={p.name} data-testid={`pillar-${p.name.toLowerCase()}`} style={{
              position: "relative",
              background: "rgba(255,255,255,0.82)",
              border: "1px solid var(--mc-border-blue)",
              borderRadius: 24,
              padding: "36px 30px 30px",
              boxShadow: "0 12px 36px rgba(14,10,31,0.05)",
              overflow: "hidden",
            }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: p.accent }} />
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: p.bg, color: p.accent,
                display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: 20,
              }}>{p.icon}</div>
              <h3 className="font-serif" style={{ fontSize: 26, fontWeight: 500, color: "var(--mc-text)", marginBottom: 12 }}>{p.name}</h3>
              <p style={{ fontSize: 14.5, color: "var(--mc-text-2)", lineHeight: 1.7, marginBottom: 18 }}>{p.body}</p>
              <div style={{ paddingTop: 16, borderTop: "0.5px solid var(--mc-border)" }}>
                <p className="font-serif" style={{
                  fontSize: 15, fontStyle: "italic", color: "var(--mc-text-3)", lineHeight: 1.55,
                }}>"{p.q}"</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────── INTELLIGENCE ────────────────────────────── */
function Intelligence() {
  const bars = [
    { label: "Communication Style", v: 87 },
    { label: "Financial Vision",    v: 62 },
    { label: "Family & Future",     v: 74 },
    { label: "Conflict Resolution", v: 55 },
    { label: "Shared Values",       v: 91 },
  ];
  return (
    <section data-testid="intelligence-section" style={{
      padding: "104px 32px",
      background: "linear-gradient(145deg, var(--mc-blue-pale) 0%, white 100%)",
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Eyebrow text="Marrsing Alignment Intelligence™" />
        <h2 className="mc-h2" style={{ fontSize: "clamp(34px, 4.2vw, 52px)", marginTop: 14, marginBottom: 56, maxWidth: 780 }}>
          This is not a quiz.<br />This is your <em>Relationship Reveal.</em>
        </h2>

        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 72, alignItems: "center",
        }} className="mc-two-col">
          <div>
            <p style={{ fontSize: 17, color: "var(--mc-text-2)", lineHeight: 1.75, marginBottom: 32 }}>
              Inside your personal Reveal Space, you receive precise, structured insight — not a generic personality report. Every pattern, every signal, every alignment point is mapped to your relationship specifically.
            </p>
            <div>
              {[
                { name: "Alignment Mapping",            tag: "Core" },
                { name: "Communication Patterns",       tag: "Core" },
                { name: "Expectation Gaps",             tag: "Core" },
                { name: "Future Vision Clarity",        tag: "Reveal" },
                { name: "Guided Conversation Prompts",  tag: "Reveal" },
                { name: "Intentional Commitment Clarity", tag: "Legacy" },
              ].map((p, i, arr) => (
                <div key={p.name} style={{
                  display: "flex", alignItems: "center", gap: 18,
                  padding: "16px 0",
                  borderBottom: i < arr.length - 1 ? "1px solid rgba(47,158,216,0.16)" : "none",
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--mc-blue)" }} />
                  <span style={{ flex: 1, fontSize: 15, fontWeight: 500, color: "var(--mc-text)" }}>{p.name}</span>
                  <span className="font-mono" style={{
                    fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
                    color: "var(--mc-blue-deep)", background: "rgba(111,199,242,0.22)",
                    padding: "4px 12px", borderRadius: 999,
                  }}>{p.tag}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{
            background: "rgba(255,255,255,0.85)",
            border: "1px solid var(--mc-border-blue)",
            borderRadius: 26, padding: 32,
            boxShadow: "0 24px 60px rgba(31,117,168,0.16)",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h4 className="font-serif" style={{ fontSize: 21, color: "var(--mc-text)", fontWeight: 500 }}>Your Alignment Reveal</h4>
              <span className="font-mono" style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase",
                background: "rgba(111,199,242,0.22)", color: "var(--mc-blue-deep)",
                padding: "5px 12px", borderRadius: 999,
              }}>
                <Lock size={11} /> Locked
              </span>
            </div>

            {bars.map((b) => (
              <div key={b.label} style={{ marginBottom: 18 }}>
                <div style={{
                  display: "flex", justifyContent: "space-between",
                  fontSize: 12.5, color: "var(--mc-text-3)", marginBottom: 6,
                }}>
                  <span>{b.label}</span>
                  <span style={{ color: "var(--mc-blue)", fontWeight: 600 }}>{b.v}%</span>
                </div>
                <div style={{ height: 6, background: "rgba(47,158,216,0.12)", borderRadius: 999, overflow: "hidden" }}>
                  <div style={{
                    width: `${b.v}%`, height: "100%",
                    background: "linear-gradient(90deg, var(--mc-blue), var(--mc-blue-light))",
                    borderRadius: 999,
                  }} />
                </div>
              </div>
            ))}
            <div style={{
              marginTop: 24, paddingTop: 18,
              borderTop: "1px solid rgba(47,158,216,0.16)",
              fontSize: 12, color: "var(--mc-text-3)",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <Sparkles size={12} color="var(--mc-blue)" />
              Responses locked until both partners reveal together
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────── PRICING ────────────────────────────── */
function Pricing() {
  const plans = [
    {
      badge: "Entry", name: "Core", price: 97,
      desc: "Begin your structured clarity experience and receive your foundational Alignment Intelligence™.",
      feats: [
        "Guided individual reflection",
        "Partner invite & locked responses",
        "Alignment Reveal dashboard",
        "Core alignment mapping",
        "Communication pattern insight",
      ],
      cta: "Begin Core Reveal",
    },
    {
      badge: "Recommended", name: "Ritual", price: 197, recommended: true,
      desc: "Go deeper with guided alignment prompts, expanded communication insights, and structured conversation frameworks.",
      feats: [
        "Everything in Core",
        "Expanded alignment categories",
        "Guided conversation prompts",
        "Expectation gap analysis",
        "Future vision clarity module",
        "30-day reflection access",
      ],
      cta: "Begin Ritual Reveal",
    },
    {
      badge: "Premium", name: "Legacy", price: 297, legacy: true,
      desc: "The complete clarity experience for long-term intentional commitment and deep relationship intelligence.",
      feats: [
        "Everything in Ritual",
        "Legacy commitment clarity",
        "Deep communication mapping",
        "Extended reflection timeline",
        "Priority reveal access",
        "Ongoing alignment check-ins",
      ],
      cta: "Begin Legacy Reveal",
    },
  ];

  return (
    <section id="pricing" data-testid="pricing-section" style={{
      padding: "104px 32px",
      background: "linear-gradient(180deg, white 0%, var(--mc-cream) 100%)",
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Eyebrow text="Founding Access" />
        <h2 className="mc-h2" style={{ fontSize: "clamp(34px, 4.2vw, 52px)", marginTop: 14, marginBottom: 14, maxWidth: 780 }}>
          Choose your level of <em>clarity.</em>
        </h2>
        <p style={{ fontSize: 16, color: "var(--mc-text-3)", marginBottom: 56 }}>
          Introductory founding access — limited release. Choose the depth your relationship deserves.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }} className="mc-three-col">
          {plans.map((p) => <PlanCard key={p.name} plan={p} />)}
        </div>
      </div>
    </section>
  );
}

function PlanCard({ plan }) {
  const isRec = plan.recommended;
  const isLeg = plan.legacy;
  return (
    <div data-testid={`plan-${plan.name.toLowerCase()}`} style={{
      position: "relative",
      background: isLeg
        ? "linear-gradient(145deg, #0D0B14 0%, #17111F 100%)"
        : isRec
          ? "linear-gradient(145deg, var(--mc-blue-pale), white)"
          : "white",
      color: isLeg ? "white" : "var(--mc-text)",
      padding: "44px 34px",
      borderRadius: 26,
      border: isRec ? "2px solid var(--mc-blue)" : isLeg ? "1px solid rgba(184,151,90,0.42)" : "1px solid var(--mc-border-blue)",
      boxShadow: isRec ? "0 22px 50px rgba(47,158,216,0.22)" : "0 14px 32px rgba(31,117,168,0.07)",
      transition: "transform 0.25s",
    }}
      onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-6px)"}
      onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
    >
      {isRec && (
        <div style={{
          position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)",
          background: "var(--mc-blue)", color: "white",
          fontSize: 10.5, fontWeight: 600, letterSpacing: "0.1em",
          textTransform: "uppercase", padding: "6px 18px", borderRadius: 999,
          fontFamily: "var(--font-mono)",
        }}>Most Recommended</div>
      )}
      <div className="font-mono" style={{
        fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
        color: isLeg ? "#D4B580" : "var(--mc-blue-deep)", fontWeight: 600,
        marginBottom: 6,
      }}>{plan.badge}</div>
      <div className="font-serif" style={{ fontSize: 30, fontWeight: 500, marginBottom: 8 }}>{plan.name}</div>
      <div className="font-serif" style={{
        fontSize: 62, fontWeight: 300, lineHeight: 1, margin: "16px 0",
        color: isLeg ? "#D4B580" : "var(--mc-blue-deep)",
      }}>
        <sup style={{ fontSize: 22, verticalAlign: "top", marginRight: 2 }}>$</sup>{plan.price}
      </div>
      <p style={{
        fontSize: 13.5, lineHeight: 1.65, marginBottom: 28,
        color: isLeg ? "rgba(255,255,255,0.6)" : "var(--mc-text-3)",
      }}>{plan.desc}</p>
      <ul style={{ listStyle: "none", padding: 0, marginBottom: 32 }}>
        {plan.feats.map((f) => (
          <li key={f} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 0",
            borderBottom: `1px solid ${isLeg ? "rgba(255,255,255,0.08)" : "rgba(47,158,216,0.12)"}`,
            fontSize: 14,
            color: isLeg ? "rgba(255,255,255,0.78)" : "var(--mc-text-2)",
          }}>
            <Check size={14} color={isLeg ? "#D4B580" : "var(--mc-blue)"} />
            {f}
          </li>
        ))}
      </ul>
      <a
        href="https://app.marrsinglife.com"
        target="_blank"
        rel="noreferrer"
        data-testid={`plan-cta-${plan.name.toLowerCase()}`}
        style={{
          display: "block", textAlign: "center",
          padding: "15px 20px", borderRadius: 999,
          fontSize: 13, fontWeight: 600, letterSpacing: "0.08em",
          textTransform: "uppercase", textDecoration: "none",
          background: isLeg ? "#D4B580" : isRec ? "var(--mc-blue-deep)" : "transparent",
          color: isLeg ? "#0D0B14" : isRec ? "white" : "var(--mc-blue-deep)",
          border: isRec || isLeg ? "1px solid transparent" : "1px solid var(--mc-blue)",
          transition: "all 0.25s",
        }}
      >
        {plan.cta}
      </a>
    </div>
  );
}

/* ────────────────────────────── TESTIMONIALS ────────────────────────────── */
function Testimonials() {
  const ts = [
    { quote: "We thought we knew each other completely. The Reveal showed us conversations we had been unconsciously avoiding for three years. It was uncomfortable and necessary.", author: "Engaged · 4 years together" },
    { quote: "I was skeptical. But reading each other's answers side by side — without defensiveness — opened something between us we never knew was closed.", author: "Married · 7 years" },
    { quote: "This is what we wish we had done before we got engaged. Not therapy — clarity. Two different things. MarrsingClause® gave us the second one.", author: "Dating intentionally · 2 years" },
  ];
  return (
    <section data-testid="testimonials-section" style={{ padding: "96px 32px", background: "white" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Eyebrow text="What Couples Say" />
        <h2 className="mc-h2" style={{ fontSize: "clamp(34px, 4.2vw, 52px)", marginTop: 14, marginBottom: 48, maxWidth: 760 }}>
          Clarity changes <em>everything.</em>
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }} className="mc-three-col">
          {ts.map((t, i) => (
            <div key={i} data-testid={`testimonial-${i}`} style={{
              background: "var(--mc-cream)",
              borderLeft: "3px solid var(--mc-blue)",
              borderRadius: 22,
              padding: 32,
            }}>
              <Quote size={20} color="var(--mc-blue)" style={{ marginBottom: 16, opacity: 0.6 }} />
              <p className="font-serif" style={{
                fontSize: 19, fontStyle: "italic", color: "var(--mc-text-2)",
                lineHeight: 1.55, marginBottom: 20,
              }}>{t.quote}</p>
              <div className="font-mono" style={{ fontSize: 11.5, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--mc-text-3)" }}>
                {t.author}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────── WAITLIST ────────────────────────────── */
function Waitlist() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({ name: name || null, email, source: "waitlist" });
      setDone(true);
      toast.success("You're on the list — we'll be in touch.");
    } catch (err) {
      toast.error("Could not save right now. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section id="waitlist" data-testid="waitlist-section" style={{
      padding: "96px 32px",
      background: "linear-gradient(180deg, white 0%, var(--mc-blue-pale) 100%)",
    }}>
      <div style={{
        maxWidth: 760, margin: "0 auto", textAlign: "center",
        background: "rgba(255,255,255,0.85)",
        border: "1px solid var(--mc-border-blue)",
        borderRadius: 28,
        padding: "56px 40px",
        boxShadow: "0 20px 50px rgba(31,117,168,0.1)",
      }}>
        <Eyebrow text="Founding Member Waitlist" center />
        <h2 className="mc-h2" style={{ fontSize: "clamp(28px, 3.6vw, 40px)", marginTop: 14, marginBottom: 14 }}>
          Be among the first.
        </h2>
        <p style={{ fontSize: 15.5, color: "var(--mc-text-3)", marginBottom: 32, maxWidth: 480, margin: "0 auto 32px" }}>
          Get early access, founding-member pricing, and invitations to private Reveal sessions before public launch.
        </p>

        {done ? (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 10,
            background: "rgba(127,205,187,0.18)",
            color: "#2C8FB7",
            border: "1px solid rgba(111,199,242,0.4)",
            padding: "16px 28px", borderRadius: 999, fontSize: 14, fontWeight: 500,
          }} data-testid="waitlist-success">
            <Sparkles size={16} /> You're on the list — check your inbox.
          </div>
        ) : (
          <form onSubmit={onSubmit} style={{
            display: "flex", flexDirection: "column", gap: 12, maxWidth: 460, margin: "0 auto",
          }}>
            <input
              data-testid="waitlist-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your first name"
              style={inputStyle}
            />
            <input
              data-testid="waitlist-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              required
              style={inputStyle}
            />
            <button data-testid="waitlist-submit" type="submit" disabled={loading} className="mc-btn-primary" style={{ width: "100%", marginTop: 4 }}>
              {loading ? "Joining…" : "Join the Waitlist"} {!loading && <ArrowRight size={16} />}
            </button>
          </form>
        )}
        <p className="font-mono" style={{ marginTop: 18, fontSize: 11, color: "var(--mc-text-3)", letterSpacing: "0.06em" }}>
          We never share your data. Unsubscribe anytime.
        </p>
      </div>
    </section>
  );
}

const inputStyle = {
  width: "100%",
  padding: "15px 18px",
  borderRadius: 14,
  border: "1px solid var(--mc-border)",
  background: "white",
  fontFamily: "var(--font-sans)",
  fontSize: 15,
  color: "var(--mc-text)",
  outline: "none",
  transition: "border-color 0.2s, box-shadow 0.2s",
  minHeight: 52,
};

/* ────────────────────────────── FINAL CTA ────────────────────────────── */
function FinalCTA() {
  return (
    <section data-testid="final-cta-section" style={{
      position: "relative",
      padding: "120px 32px",
      background: `
        radial-gradient(circle at 50% 18%, rgba(111,199,242,0.42), transparent 38%),
        linear-gradient(135deg, var(--mc-blue-deep) 0%, var(--mc-blue) 100%)
      `,
      overflow: "hidden",
      textAlign: "center",
    }}>
      <div className="font-serif" style={{
        position: "absolute", left: "50%", top: "50%",
        transform: "translate(-50%, -50%)",
        fontSize: 580, fontWeight: 300, color: "rgba(255,255,255,0.07)",
        pointerEvents: "none", userSelect: "none", lineHeight: 1,
      }}>M</div>

      <div style={{ position: "relative", zIndex: 1, maxWidth: 720, margin: "0 auto" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
          <span style={{ width: 36, height: 1, background: "rgba(255,255,255,0.75)" }} />
          <span className="font-mono" style={{
            fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "white", fontWeight: 600,
          }}>Your Moment</span>
          <span style={{ width: 36, height: 1, background: "rgba(255,255,255,0.75)" }} />
        </div>
        <h2 className="font-serif" style={{
          fontSize: "clamp(36px, 5vw, 60px)", fontWeight: 300, lineHeight: 1.12,
          color: "white", marginBottom: 20,
        }}>
          If you are ready for clarity,<br /><em style={{ color: "var(--mc-blue-pale)" }}>this is your moment.</em>
        </h2>
        <p style={{ fontSize: 17, color: "rgba(255,255,255,0.88)", maxWidth: 560, margin: "0 auto 40px", lineHeight: 1.7 }}>
          MarrsingClause® is opening access in phases. Begin your Clarity &amp; Alignment Reveal while introductory founding access is available.
        </p>
        <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to="/quiz" className="mc-btn-gold" data-testid="final-cta-quiz">
            Take the Free Clarity Quiz <ArrowRight size={16} />
          </Link>
          <a href="https://app.marrsinglife.com" target="_blank" rel="noreferrer" className="mc-btn-secondary" data-testid="final-cta-app" style={{
            background: "transparent", color: "white", borderColor: "rgba(255,255,255,0.55)",
          }}>
            Enter the MarrsingClause® App
          </a>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────── SHARED ────────────────────────────── */
function Eyebrow({ text, center = false }) {
  return (
    <div style={{
      display: center ? "flex" : "inline-flex",
      justifyContent: center ? "center" : "flex-start",
      alignItems: "center", gap: 14,
    }}>
      <span style={{ width: 32, height: 1, background: "var(--mc-blue)" }} />
      <span className="mc-eyebrow">{text}</span>
      {center && <span style={{ width: 32, height: 1, background: "var(--mc-blue)" }} />}
    </div>
  );
}
